<template>
  <div class="h-screen flex relative overflow-hidden bg-surface-50 dark:bg-surface-950 dark:circuit-bg">
    <!-- Sidebar - Fixed with tech panel styling -->
    <DashboardSidebar
      ref="sidebarRef"
      :is-admin="isAdmin"
      :is-band-admin="isBandAdmin"
      :is-redshirt-admin="isRedshirtAdmin"
      :is-redshirt-lieutenant="isRedshirtLieutenant"
      :is-checkin-volunteer="isCheckinVolunteer"
      :is-dark-mode="isDarkMode"
      :has-subkrewe="hasSubkrewe"
      class="tech-panel z-10"
      @toggle-dark-mode="toggleDarkMode"
    />

    <div class="h-screen flex flex-col relative flex-auto">
      <!-- TopBar - Fixed with tech styling -->
      <DashboardTopBar
        :display-name="displayName"
        :is-admin="isAdmin"
        class="flex-shrink-0 tech-panel border-b border-primary-500/30 backdrop-blur-sm z-10"
      />

      <!-- Main Content Area - Scrollable with enhanced grid background -->
      <div class="relative flex-1 overflow-auto bg-grid-pattern">
        <div class="p-2 h-full min-h-0">
          <div class="tech-card h-full">
            <slot />
          </div>
        </div>
      </div>

      <!-- Drawer - Fixed with internal scrolling -->
      <DashboardDrawer
        :is-open="isDrawerOpen"
        :title="drawerTitle"
        :content="drawerContent"
        :content-props="{}"
        :can-go-back="canGoBack"
        @close="closeDrawer"
        @back="goBackInDrawer"
        @reset-history="resetDrawerHistory"
      />
    </div>
    <Toast class="!tech-panel" />
    <ConfirmDialog />

    <!-- Enhanced animated background elements -->
    <div class="stars-container fixed inset-0 pointer-events-none" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from 'vue'
import { useRouter } from 'vue-router'

const isDarkMode = ref(false)
const sidebarRef = ref(null)
const router = useRouter()

// Get current user and reactive member data from store
const currentUser = useCurrentUser()
const membersStore = useMembersStore()

// Get current member from store reactively
const memberPath = computed(() => {
  const uid = currentUser.value?.uid
  return uid ? `members/${uid}` : null
})

// Get member data from store using the path - this is reactive!
const currentMember = computed(() => {
  if (!memberPath.value) return null
  return membersStore.members[memberPath.value] || null
})

// Reactive computed properties based on member data
const displayName = computed(() => currentMember.value?.name || '')
const memberRoles = computed(() => currentMember.value?.roles || {})
const isAdmin = computed(() => memberRoles.value?.admin === true)
const isBandAdmin = computed(() => memberRoles.value?.band_admin === true)
const isRedshirtAdmin = computed(() => memberRoles.value?.redshirt_admin === true)
const isRedshirtLieutenant = computed(() => currentMember.value?.isRedshirtLieutenant === true)
const isCheckinVolunteer = computed(() => memberRoles.value?.checkinVolunteer === true || memberRoles.value?.admin === true)
const hasSubkrewe = computed(() => !!currentMember.value?.subkrewe?.id)

// Method to close the sidebar (will be called when a link is clicked)
const closeSidebar = () => {
  // Check if we're on mobile (non-desktop)
  if (window.innerWidth < 1024) {
    document.getElementById('app-sidebar-9')?.classList.add('hidden')
  }
}

onMounted(async () => {
  // Check localStorage first, then fallback to classList
  const storedTheme = localStorage.getItem('theme')
  if (storedTheme === 'dark') {
    document.documentElement.classList.add('dark')
    isDarkMode.value = true
  }
  else if (storedTheme === 'light') {
    document.documentElement.classList.remove('dark')
    isDarkMode.value = false
  }
  else {
    // Fallback to current state if no stored preference
    isDarkMode.value = document.documentElement.classList.contains('dark')
  }

  // Ensure current member is loaded into the store
  if (currentUser.value?.uid) {
    await membersStore.fetchMember(currentUser.value.uid)
  }

  // Listen for route changes to close the sidebar
  if (router) {
    router.afterEach(() => {
      closeSidebar()
    })
  }
})

// Use the drawer composable with the new functionality
const {
  isDrawerOpen,
  drawerTitle,
  drawerContent,
  canGoBack,
  closeDrawer,
  goBackInDrawer,
  resetDrawerHistory,
} = useDrawer()

function toggleDarkMode() {
  document.documentElement.classList.toggle('dark')
  isDarkMode.value = !isDarkMode.value
  // Store the preference
  localStorage.setItem('theme', isDarkMode.value ? 'dark' : 'light')
}

// Provide memberRoles to child components
provide('memberRoles', memberRoles)
</script>

<style>
/* Global styles to ensure proper scrolling behavior for DataTable */
.dashboard-content {
  width: fit-content;
  min-width: 100%;
}

/* Ensure other components don't get clipped */
.card {
  overflow: visible;
  width: fit-content;
  min-width: 100%;
}

/* Add sci-fi background grid pattern */
.bg-grid-pattern {
  background-color: rgba(0, 0, 0, 0.02);
  background-image:
    linear-gradient(rgba(57, 255, 20, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(57, 255, 20, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  background-position: center center;
}

.dark .bg-grid-pattern {
  background-color: rgba(0, 0, 0, 0.2);
  background-image:
    linear-gradient(rgba(57, 255, 20, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(57, 255, 20, 0.07) 1px, transparent 1px);
}

/* Animated stars background */
.stars-container {
  background: transparent;
  background-image: radial-gradient(2px 2px at 20px 30px, #eee, rgba(0,0,0,0)),
                    radial-gradient(2px 2px at 40px 70px, #fff, rgba(0,0,0,0)),
                    radial-gradient(1px 1px at 90px 40px, #fff, rgba(0,0,0,0));
  background-repeat: repeat;
  background-size: 200px 200px;
  opacity: 0.1;
  z-index: 1;
}

.dark .stars-container {
  opacity: 0.5;
  animation: movingStars 150s linear infinite;
}

@keyframes movingStars {
  from { background-position: 0 0; }
  to { background-position: 200px 200px; }
}
</style>

<style scoped>
/* Adjust flex properties to prevent excessive expansion */
.flex-auto {
  max-width: 100%;
  overflow-x: hidden;
}

.flex-1 {
  max-width: 100%;
  overflow-x: hidden;
}
</style>
